import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { FiArrowLeft, FiBook, FiCheck, FiInfo } from "react-icons/fi";
import { Editor } from "@tinymce/tinymce-react";
import { server, tinyApiKey } from "../../server";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { motion } from "framer-motion";

export default function AddNewDoc() {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const [content, setContent] = useState("");
  const [contentError, setContentError] = useState("");
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await axios.get(`${server}/categories/all`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setCategories(data?.data || []);
      } catch (err) {
        toast.error("Failed to load categories list");
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleAddDoc = async (data) => {
    // Validate TinyMCE editor content (must not be empty/just whitespace)
    const strippedContent = content.replace(/<[^>]*>/g, "").trim();
    if (!strippedContent) {
      setContentError("Document content is required");
      return;
    }
    setContentError("");
    setLoading(true);

    try {
      const { data: resData } = await axios.post(
        `${server}/docs/new`,
        content.toString() || "",
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          params: {
            title: data?.title,
            coverImageUrl: "",
            category: data?.category,
          },
        }
      );
      toast.success(resData?.message || "New document created successfully!");
      reset();
      setContent("");
      setLoading(false);
      navigate("../docsList");
    } catch (e) {
      setLoading(false);
      toast.error(e?.response?.data?.message || e?.message);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 font-sans">
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-6xl mx-auto"
      >
        {/* Back navigation */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-800 font-medium text-sm transition mb-6 group"
        >
          <FiArrowLeft className="group-hover:-translate-x-1 transition duration-200" />
          Back to list
        </button>

        {/* Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight flex items-center gap-3">
            <span className="p-2 bg-blue-50 text-blue-600 rounded-xl">
              <FiBook className="text-2xl" />
            </span>
            Create New Document
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Write clean, professional and structured documents for your team.
          </p>
        </div>

        {/* Two-Column Editor Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
          {/* Main Form (Left 3 columns) */}
          <form
            onSubmit={handleSubmit(handleAddDoc)}
            className="lg:col-span-3 space-y-6"
          >
            <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6">
              {/* Metadata Group */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Title Input */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                    Document Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. API Integration Guide"
                    {...register("title", { required: "Title is required" })}
                    className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition ${
                      errors.title ? "border-red-300 focus:ring-red-500/10 focus:border-red-500" : "border-slate-200"
                    }`}
                  />
                  {errors.title && (
                    <p className="text-red-500 text-xs mt-1.5 font-medium">
                      {errors.title.message}
                    </p>
                  )}
                </div>

                 {/* Category Input */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => navigate("../categoryManager")}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline transition"
                    >
                      Manage Categories
                    </button>
                  </div>
                  {categoriesLoading ? (
                    <div className="w-full h-10 bg-slate-100 rounded-xl animate-pulse" />
                  ) : (
                    <select
                      {...register("category", { required: "Category is required" })}
                      className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white transition cursor-pointer ${
                        errors.category ? "border-red-300 focus:ring-red-500/10 focus:border-red-500" : "border-slate-200"
                      }`}
                    >
                      <option value="">Select a category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.name}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.category && (
                    <p className="text-red-500 text-xs mt-1.5 font-medium">
                      {errors.category.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Editor Group */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Document Content <span className="text-red-500">*</span>
                </label>
                <div className={`rounded-2xl overflow-hidden border ${
                  contentError ? "border-red-300" : "border-slate-200 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500"
                }`}>
                  <Editor
                    apiKey={tinyApiKey}
                    init={{
                      height: 500,
                      menubar: true,
                      branding: false,
                      plugins:
                        "advlist autolink lists link image charmap print preview anchor " +
                        "searchreplace visualblocks code fullscreen " +
                        "insertdatetime media table paste code help wordcount",
                      toolbar:
                        "undo redo | formatselect | bold italic underline strikethrough | " +
                        "alignleft aligncenter alignright alignjustify | " +
                        "bullist numlist outdent indent | " +
                        "link image media table | " +
                        "forecolor backcolor removeformat | " +
                        "charmap fullscreen preview code help",
                      image_advtab: true,
                      content_style:
                        "body { font-family:-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; font-size:14px; line-height:1.6; color:#334155; padding: 12px; }",
                    }}
                    value={content}
                    onEditorChange={(content) => {
                      setContent(content);
                      if (content.replace(/<[^>]*>/g, "").trim()) {
                        setContentError("");
                      }
                    }}
                  />
                </div>
                {contentError && (
                  <p className="text-red-500 text-xs mt-1.5 font-medium">
                    {contentError}
                  </p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 active:bg-slate-100 transition duration-150"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl text-sm font-semibold shadow-sm hover:shadow transition duration-150"
              >
                {loading ? (
                  "Submitting..."
                ) : (
                  <>
                    <FiCheck />
                    Submit Document
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Quick Guide Card (Right Column) */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
              <div className="flex items-center gap-2 text-slate-800 font-bold text-sm mb-4 border-b border-slate-100 pb-3">
                <FiInfo className="text-blue-500 text-base" />
                <span>Documentation Guide</span>
              </div>
              <ul className="space-y-4 text-xs text-slate-500">
                <li className="flex gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 shrink-0" />
                  <span>Use clear, descriptive titles that explain the document's goal.</span>
                </li>
                <li className="flex gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 shrink-0" />
                  <span>Choose accurate categories (e.g. `User Manual`, `Technical API`) for easy searching.</span>
                </li>
                <li className="flex gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 shrink-0" />
                  <span>Use rich-text headers (H2, H3) to divide long pages logically.</span>
                </li>
                <li className="flex gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 shrink-0" />
                  <span>Format code chunks using formatting tags, or create simple structured tables.</span>
                </li>
                <li className="flex gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 shrink-0" />
                  <span>Revisions will be tracked automatically with your user name.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
